import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import {
  DEFAULT_WEBSITE_LOGO,
  DEFAULT_THEME,
  FONT_OPTIONS,
  THEME_PRESETS,
  ThemePreset,
  ThemeOverrides,
  loadThemeOverrides,
  loadCustomThemePresets,
  loadWebsiteLogo,
  resetWebsiteLogo,
  resetThemeOverrides,
  saveCustomThemePresets,
  saveThemeOverrides,
  saveWebsiteLogo,
} from './themeRuntime';
import { ADMIN_CONSOLE_COPY, THEME_DESIGNER_COPY, storedUiLanguage } from './i18n/consoleChromeCopy';
import BrandLockup from './BrandLockup';

type ThemeTokenField = {
  key: keyof ThemeOverrides;
  label: string;
  type: 'color' | 'text' | 'select';
  options?: Array<{ label: string; value: string }>;
};

const COLOR_FIELDS: ThemeTokenField[] = [
  { key: '--color-canvas', label: 'Canvas', type: 'color' },
  { key: '--color-canvas-alt', label: 'Canvas Alt', type: 'color' },
  { key: '--color-surface', label: 'Surface', type: 'color' },
  { key: '--color-surface-muted', label: 'Surface Muted', type: 'color' },
  { key: '--color-border', label: 'Border', type: 'color' },
  { key: '--color-border-strong', label: 'Border Strong', type: 'color' },
  { key: '--color-primary', label: 'Primary', type: 'color' },
  { key: '--color-primary-strong', label: 'Primary Strong', type: 'color' },
  { key: '--color-primary-soft', label: 'Primary Soft', type: 'color' },
  { key: '--color-accent', label: 'Accent', type: 'color' },
  { key: '--color-success', label: 'Success', type: 'color' },
  { key: '--color-danger', label: 'Danger', type: 'color' },
  { key: '--color-text-primary', label: 'Text Primary', type: 'color' },
  { key: '--color-text-strong', label: 'Text Strong', type: 'color' },
  { key: '--color-text-muted', label: 'Text Muted', type: 'color' },
];

const STYLE_FIELDS: ThemeTokenField[] = [
  { key: '--font-sans', label: 'Base Font', type: 'select', options: FONT_OPTIONS },
  { key: '--radius-sm', label: 'Radius Small', type: 'text' },
  { key: '--radius-md', label: 'Radius Medium', type: 'text' },
  { key: '--radius-lg', label: 'Radius Large', type: 'text' },
];

const hasDesignerAccess = (user: ReturnType<typeof useAuth>['user']): boolean => {
  if (!user) {
    return false;
  }
  const roles = new Set([
    ...(user.platform_roles || []),
    ...(user.effective_roles || []),
  ]);
  return user.is_platform_admin || roles.has('designer') || roles.has('design_admin');
};

const normalizeHex = (value: string): string => {
  const raw = value.trim().replace('#', '');
  if (raw.length === 3) {
    return `#${raw.split('').map((part) => `${part}${part}`).join('').toUpperCase()}`;
  }
  if (raw.length === 6) {
    return `#${raw.toUpperCase()}`;
  }
  return '#000000';
};

const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const normalized = normalizeHex(hex).slice(1);
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
};

const rgbToHex = (r: number, g: number, b: number): string => {
  const toHex = (value: number) => Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, '0').toUpperCase();
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const rgbToHsl = (r: number, g: number, b: number): { h: number; s: number; l: number } => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;

  let h = 0;
  const l = (max + min) / 2;
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));

  if (delta !== 0) {
    if (max === rn) {
      h = 60 * (((gn - bn) / delta) % 6);
    } else if (max === gn) {
      h = 60 * (((bn - rn) / delta) + 2);
    } else {
      h = 60 * (((rn - gn) / delta) + 4);
    }
  }

  if (h < 0) {
    h += 360;
  }

  return { h, s, l };
};

const hslToRgb = (h: number, s: number, l: number): { r: number; g: number; b: number } => {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let rPrime = 0;
  let gPrime = 0;
  let bPrime = 0;

  if (h >= 0 && h < 60) {
    rPrime = c;
    gPrime = x;
  } else if (h >= 60 && h < 120) {
    rPrime = x;
    gPrime = c;
  } else if (h >= 120 && h < 180) {
    gPrime = c;
    bPrime = x;
  } else if (h >= 180 && h < 240) {
    gPrime = x;
    bPrime = c;
  } else if (h >= 240 && h < 300) {
    rPrime = x;
    bPrime = c;
  } else {
    rPrime = c;
    bPrime = x;
  }

  return {
    r: (rPrime + m) * 255,
    g: (gPrime + m) * 255,
    b: (bPrime + m) * 255,
  };
};

const shiftHue = (baseHex: string, offsetDegrees: number): string => {
  const { r, g, b } = hexToRgb(baseHex);
  const hsl = rgbToHsl(r, g, b);
  const nextHue = (hsl.h + offsetDegrees + 360) % 360;
  const rgb = hslToRgb(nextHue, hsl.s, hsl.l);
  return rgbToHex(rgb.r, rgb.g, rgb.b);
};

const stripUniformMatteBackground = (
  imageData: ImageData,
): ImageData => {
  const { data, width, height } = imageData;
  const sampleSize = Math.max(1, Math.round(Math.min(width, height) * 0.03));
  const corners = [
    [0, 0],
    [Math.max(0, width - sampleSize), 0],
    [0, Math.max(0, height - sampleSize)],
    [Math.max(0, width - sampleSize), Math.max(0, height - sampleSize)],
  ];

  const samples: Array<{ r: number; g: number; b: number; a: number }> = [];
  corners.forEach(([startX, startY]) => {
    for (let y = startY; y < Math.min(height, startY + sampleSize); y += 1) {
      for (let x = startX; x < Math.min(width, startX + sampleSize); x += 1) {
        const idx = (y * width + x) * 4;
        samples.push({
          r: data[idx],
          g: data[idx + 1],
          b: data[idx + 2],
          a: data[idx + 3],
        });
      }
    }
  });

  if (!samples.length) {
    return imageData;
  }

  const avg = samples.reduce(
    (acc, cur) => ({
      r: acc.r + cur.r,
      g: acc.g + cur.g,
      b: acc.b + cur.b,
      a: acc.a + cur.a,
    }),
    { r: 0, g: 0, b: 0, a: 0 },
  );
  const count = samples.length;
  const matte = {
    r: avg.r / count,
    g: avg.g / count,
    b: avg.b / count,
    a: avg.a / count,
  };

  const matteIsOpaque = matte.a > 245;
  if (!matteIsOpaque) {
    return imageData;
  }

  const matteLooksNeutral =
    Math.abs(matte.r - matte.g) < 14 &&
    Math.abs(matte.g - matte.b) < 14 &&
    (matte.r < 28 || matte.r > 227);
  if (!matteLooksNeutral) {
    return imageData;
  }

  const tolerance = matte.r < 80 ? 26 : 20;
  let removablePixelCount = 0;
  const totalPixels = width * height;
  const next = new Uint8ClampedArray(data);

  for (let i = 0; i < totalPixels; i += 1) {
    const idx = i * 4;
    const r = data[idx];
    const g = data[idx + 1];
    const b = data[idx + 2];
    const a = data[idx + 3];
    const closeToMatte =
      Math.abs(r - matte.r) <= tolerance &&
      Math.abs(g - matte.g) <= tolerance &&
      Math.abs(b - matte.b) <= tolerance &&
      a > 240;

    if (closeToMatte) {
      next[idx + 3] = 0;
      removablePixelCount += 1;
    }
  }

  // Only strip when the matte dominates enough to avoid deleting real dark logo art.
  if (removablePixelCount / totalPixels < 0.3) {
    return imageData;
  }

  return new ImageData(next, width, height);
};

const trimTransparentMargins = async (sourceDataUrl: string): Promise<string> => {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const nextImage = new Image();
    nextImage.onload = () => resolve(nextImage);
    nextImage.onerror = () => reject(new Error('Unable to process logo image.'));
    nextImage.src = sourceDataUrl;
  });

  const sourceCanvas = document.createElement('canvas');
  sourceCanvas.width = image.naturalWidth;
  sourceCanvas.height = image.naturalHeight;
  const sourceContext = sourceCanvas.getContext('2d');
  if (!sourceContext) {
    return sourceDataUrl;
  }

  sourceContext.drawImage(image, 0, 0);
  const rawImageData = sourceContext.getImageData(0, 0, sourceCanvas.width, sourceCanvas.height);
  const cleanedImageData = stripUniformMatteBackground(rawImageData);
  sourceContext.putImageData(cleanedImageData, 0, 0);
  const { data, width, height } = cleanedImageData;

  let left = width;
  let right = -1;
  let top = height;
  let bottom = -1;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const alpha = data[(y * width + x) * 4 + 3];
      if (alpha > 6) {
        if (x < left) left = x;
        if (x > right) right = x;
        if (y < top) top = y;
        if (y > bottom) bottom = y;
      }
    }
  }

  if (right < left || bottom < top) {
    return sourceDataUrl;
  }

  const contentWidth = right - left + 1;
  const contentHeight = bottom - top + 1;
  const padding = Math.max(2, Math.round(Math.max(contentWidth, contentHeight) * 0.04));

  const targetCanvas = document.createElement('canvas');
  targetCanvas.width = contentWidth + padding * 2;
  targetCanvas.height = contentHeight + padding * 2;
  const targetContext = targetCanvas.getContext('2d');
  if (!targetContext) {
    return sourceDataUrl;
  }

  targetContext.drawImage(
    sourceCanvas,
    left,
    top,
    contentWidth,
    contentHeight,
    padding,
    padding,
    contentWidth,
    contentHeight,
  );

  return targetCanvas.toDataURL('image/png');
};

type LogoFitResult = {
  dataUrl: string;
  suggestion: string;
};

const autoFitLogoWithAiSuggestion = async (sourceDataUrl: string): Promise<LogoFitResult> => {
  const trimmed = await trimTransparentMargins(sourceDataUrl);
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const nextImage = new Image();
    nextImage.onload = () => resolve(nextImage);
    nextImage.onerror = () => reject(new Error('Unable to process logo image.'));
    nextImage.src = trimmed;
  });

  const sourceWidth = Math.max(1, image.naturalWidth);
  const sourceHeight = Math.max(1, image.naturalHeight);
  const aspectRatio = sourceWidth / sourceHeight;
  const isWideMark = aspectRatio >= 2.4;

  // Heuristic "AI" suggestion: infer lockup style and pick a site-friendly export frame.
  const targetWidth = isWideMark ? 820 : 560;
  const targetHeight = isWideMark ? 220 : 320;
  const fitScale = Math.min(targetWidth / sourceWidth, targetHeight / sourceHeight);
  const drawWidth = Math.max(1, Math.round(sourceWidth * fitScale));
  const drawHeight = Math.max(1, Math.round(sourceHeight * fitScale));
  const offsetX = Math.round((targetWidth - drawWidth) / 2);
  const offsetY = Math.round((targetHeight - drawHeight) / 2);

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const context = canvas.getContext('2d');
  if (!context) {
    return {
      dataUrl: trimmed,
      suggestion: 'AI fit could not be applied; uploaded original trim only.',
    };
  }

  context.clearRect(0, 0, targetWidth, targetHeight);
  context.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);

  const suggestion = isWideMark
    ? 'AI suggestion: widescreen lockup detected. Applied centered 820x220 framing for stronger header presence.'
    : 'AI suggestion: stacked/compact mark detected. Applied centered 560x320 framing for better readability.';

  return {
    dataUrl: canvas.toDataURL('image/png'),
    suggestion,
  };
};

const ThemeDesignerPage: React.FC = () => {
  const chrome = THEME_DESIGNER_COPY[storedUiLanguage()];
  const adminChrome = ADMIN_CONSOLE_COPY[storedUiLanguage()];
  const { user } = useAuth();
  const [formValues, setFormValues] = React.useState<ThemeOverrides>(() => ({ ...DEFAULT_THEME, ...loadThemeOverrides() }));
  const [logoPreview, setLogoPreview] = React.useState<string>(() => loadWebsiteLogo());
  const [customThemePresets, setCustomThemePresets] = React.useState<ThemePreset[]>(() => loadCustomThemePresets());
  const [newThemeName, setNewThemeName] = React.useState<string>('');
  const [message, setMessage] = React.useState<string>('');

  const allThemePresets = React.useMemo(() => {
    return [...THEME_PRESETS, ...customThemePresets];
  }, [customThemePresets]);

  const saveCustomPresets = React.useCallback((nextPresets: ThemePreset[]) => {
    setCustomThemePresets(nextPresets);
    saveCustomThemePresets(nextPresets);
  }, []);

  const activeThemeId = React.useMemo(() => {
    const mergedCurrent = { ...DEFAULT_THEME, ...formValues };
    const matchedPreset = allThemePresets.find((preset) => {
      const mergedPreset = { ...DEFAULT_THEME, ...preset.overrides };
      return JSON.stringify(mergedPreset) === JSON.stringify(mergedCurrent);
    });
    return matchedPreset?.id ?? 'custom';
  }, [allThemePresets, formValues]);

  const activeThemeLabel = React.useMemo(() => {
    if (activeThemeId === 'custom') {
      return 'Custom Theme';
    }
    return allThemePresets.find((preset) => preset.id === activeThemeId)?.label ?? 'Custom Theme';
  }, [activeThemeId, allThemePresets]);

  const activeCustomPreset = React.useMemo(() => {
    return customThemePresets.find((preset) => preset.id === activeThemeId) || null;
  }, [activeThemeId, customThemePresets]);

  const basePrimaryColor = normalizeHex(formValues['--color-primary'] || DEFAULT_THEME['--color-primary']);
  const complementaryColor = shiftHue(basePrimaryColor, 180);
  const analogousA = shiftHue(basePrimaryColor, -30);
  const analogousB = shiftHue(basePrimaryColor, 30);
  const triadicA = shiftHue(basePrimaryColor, 120);
  const triadicB = shiftHue(basePrimaryColor, -120);

  if (!hasDesignerAccess(user)) {
    return (
      <section className="page-section">
        <div className="theme-admin-card">
          <h1>{chrome.restricted}</h1>
          <p>{chrome.restrictedBody}</p>
        </div>
      </section>
    );
  }

  const onChange = (key: keyof ThemeOverrides, value: string) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <section className="page-section">
      <div className="theme-admin-card">
        <nav className="admin-console-breadcrumbs" aria-label={adminChrome.breadcrumb}>
          <Link to="/console">{adminChrome.console}</Link>
          <span>/</span>
          <Link to="/console/style">Style governance</Link>
          <span>/</span>
          <span className="admin-console-breadcrumb-current">{chrome.title}</span>
        </nav>
        <h1>{chrome.title}</h1>
        <p>
          Standardize typography, color, and components with accessibility and visual consistency guardrails.
        </p>
        <ul className="dashboard-payment-best-practices">
          <li>WCAG 1.4.3: keep text contrast at least 4.5:1 for normal text.</li>
          <li>WCAG 2.4.8: expose location context with breadcrumbs and clear headings.</li>
          <li>Define design tokens (color, radius, typography) as the single source of truth.</li>
          <li>Keep navigation order and naming consistent across all admin experiences.</li>
        </ul>
        <p>Update the shared website palette, typography, and shape tokens. Changes apply globally and persist in browser storage.</p>
        <p><strong>Note:</strong> Logo uploads are currently stored in your browser (local storage), not in a shared server asset store.</p>
        <p><strong>Active theme:</strong> {activeThemeLabel}</p>

        <h2>{chrome.logo}</h2>
        <div className="theme-admin-logo-panel">
          <BrandLockup path={logoPreview || DEFAULT_WEBSITE_LOGO} imageClassName="theme-admin-logo-preview" />
          <div className="theme-admin-logo-actions">
            <label className="btn btn-secondary" htmlFor="website-logo-upload">Upload logo</label>
            <input
              id="website-logo-upload"
              type="file"
              accept="image/*"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (!file) {
                  return;
                }
                const reader = new FileReader();
                reader.onload = async () => {
                  const nextLogo = typeof reader.result === 'string' ? reader.result : DEFAULT_WEBSITE_LOGO;
                  try {
                    const aiFit = await autoFitLogoWithAiSuggestion(nextLogo);
                    setLogoPreview(aiFit.dataUrl);
                    saveWebsiteLogo(aiFit.dataUrl);
                    setMessage(`Website logo updated. ${aiFit.suggestion}`);
                  } catch {
                    setLogoPreview(nextLogo);
                    saveWebsiteLogo(nextLogo);
                    setMessage('Website logo updated.');
                  }
                };
                reader.readAsDataURL(file);
              }}
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setLogoPreview(DEFAULT_WEBSITE_LOGO);
                resetWebsiteLogo();
                setMessage('Website logo reset to default.');
              }}
            >
              Reset logo
            </button>
          </div>
        </div>

        <h2>{chrome.themeLibrary}</h2>
        <div className="theme-admin-presets">
          {allThemePresets.map((preset) => {
            const isCustom = customThemePresets.some((item) => item.id === preset.id);
            return (
              <div key={preset.id} className={`theme-preset-item ${activeThemeId === preset.id ? 'theme-preset-active' : ''}`}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    const merged = { ...DEFAULT_THEME, ...preset.overrides };
                    setFormValues(merged);
                    saveThemeOverrides(merged);
                    setMessage(`Applied theme: ${preset.label}`);
                  }}
                >
                  {preset.label}
                </button>
                {isCustom ? (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      const nextPresets = customThemePresets.filter((item) => item.id !== preset.id);
                      saveCustomPresets(nextPresets);
                      setMessage(`Deleted theme: ${preset.label}`);
                    }}
                  >
                    Delete
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>

        <div className="theme-admin-theme-editor">
          <label className="theme-admin-field">
            <span>New theme name</span>
            <input
              type="text"
              value={newThemeName}
              onChange={(event) => setNewThemeName(event.target.value)}
              placeholder="e.g. Ocean Calm"
            />
          </label>
          <div className="theme-admin-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                const trimmedName = newThemeName.trim();
                if (!trimmedName) {
                  setMessage('Enter a theme name before adding.');
                  return;
                }
                const nextPreset: ThemePreset = {
                  id: `custom-${Date.now()}`,
                  label: trimmedName,
                  overrides: { ...formValues },
                };
                const nextPresets = [...customThemePresets, nextPreset];
                saveCustomPresets(nextPresets);
                setNewThemeName('');
                setMessage(`Added theme: ${trimmedName}`);
              }}
            >
              Add Theme
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={!activeCustomPreset}
              onClick={() => {
                if (!activeCustomPreset) {
                  setMessage('Apply a custom theme before modifying it.');
                  return;
                }
                const nextPresets = customThemePresets.map((preset) => {
                  if (preset.id !== activeCustomPreset.id) {
                    return preset;
                  }
                  return { ...preset, overrides: { ...formValues } };
                });
                saveCustomPresets(nextPresets);
                setMessage(`Updated theme: ${activeCustomPreset.label}`);
              }}
            >
              Update Active Theme
            </button>
          </div>
        </div>

        <h2>{chrome.colorTheory}</h2>
        <p>Based on RGB color-wheel harmony rules (complementary, analogous, triadic), use these as accent candidates for your selected primary color.</p>
        <div className="theme-admin-color-theory-grid">
          <div className="theme-admin-color-swatch-card">
            <strong>Primary</strong>
            <span className="theme-admin-color-swatch" style={{ backgroundColor: basePrimaryColor }} />
            <code>{basePrimaryColor}</code>
          </div>
          <div className="theme-admin-color-swatch-card">
            <strong>Complementary</strong>
            <span className="theme-admin-color-swatch" style={{ backgroundColor: complementaryColor }} />
            <code>{complementaryColor}</code>
          </div>
          <div className="theme-admin-color-swatch-card">
            <strong>Analogous A</strong>
            <span className="theme-admin-color-swatch" style={{ backgroundColor: analogousA }} />
            <code>{analogousA}</code>
          </div>
          <div className="theme-admin-color-swatch-card">
            <strong>Analogous B</strong>
            <span className="theme-admin-color-swatch" style={{ backgroundColor: analogousB }} />
            <code>{analogousB}</code>
          </div>
          <div className="theme-admin-color-swatch-card">
            <strong>Triadic A</strong>
            <span className="theme-admin-color-swatch" style={{ backgroundColor: triadicA }} />
            <code>{triadicA}</code>
          </div>
          <div className="theme-admin-color-swatch-card">
            <strong>Triadic B</strong>
            <span className="theme-admin-color-swatch" style={{ backgroundColor: triadicB }} />
            <code>{triadicB}</code>
          </div>
        </div>

        <h2>{chrome.colorPalette}</h2>
        <div className="theme-admin-grid">
          {COLOR_FIELDS.map((field) => (
            <label key={field.key} className="theme-admin-field">
              <span>{field.label}</span>
              <input
                type="color"
                value={formValues[field.key] || DEFAULT_THEME[field.key] || '#000000'}
                onChange={(event) => onChange(field.key, event.target.value)}
              />
              <code>{field.key}</code>
            </label>
          ))}
        </div>

        <h2>{chrome.typography}</h2>
        <div className="theme-admin-grid theme-admin-grid-compact">
          {STYLE_FIELDS.map((field) => {
            if (field.type === 'select') {
              return (
                <label key={field.key} className="theme-admin-field">
                  <span>{field.label}</span>
                  <select
                    value={formValues[field.key] || DEFAULT_THEME[field.key] || ''}
                    onChange={(event) => onChange(field.key, event.target.value)}
                  >
                    {(field.options || []).map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <code>{field.key}</code>
                </label>
              );
            }

            return (
              <label key={field.key} className="theme-admin-field">
                <span>{field.label}</span>
                <input
                  type="text"
                  value={formValues[field.key] || DEFAULT_THEME[field.key] || ''}
                  onChange={(event) => onChange(field.key, event.target.value)}
                  placeholder="e.g. 12px"
                />
                <code>{field.key}</code>
              </label>
            );
          })}
        </div>

        <div className="theme-admin-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              saveThemeOverrides(formValues);
              setMessage('Theme overrides saved.');
            }}
          >
            Save Theme
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              resetThemeOverrides();
              setFormValues({ ...DEFAULT_THEME });
              setMessage('Theme reset to defaults.');
            }}
          >
            Reset to Default
          </button>
        </div>

        {message ? <p className="theme-admin-message">{message}</p> : null}
      </div>
    </section>
  );
};

export default ThemeDesignerPage;
